import React, { useState, useRef, useMemo, useEffect } from "react";

export function UIInput(
  props: React.InputHTMLAttributes<HTMLInputElement> & { value?: any }
) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  // grayscale palette
  const BORDER_DEFAULT = "#c9ced6"; // light gray
  const BORDER_HOVER = "#b3b9c3"; // slightly darker
  const BORDER_FOCUS = "#9aa2af"; // darker gray (still gray, not blue)
  const BG_SOFT = "#f5f6fa"; // stays always

  const style: React.CSSProperties = {
    width: "100%",
    height: 40,
    borderRadius: 10,
    padding: "8px 12px",
    outline: "none",
    border: "1px solid",
    borderColor: focus ? BORDER_FOCUS : hover ? BORDER_HOVER : BORDER_DEFAULT,
    background: BG_SOFT,                // <-- always soft gray
    transition: "border-color .15s ease",
    boxShadow: "none",                  // <-- no blue ring
    color: "#111827",
    ...props.style,
  };

  return (
    <input
      {...props}
      style={style}
      onMouseEnter={(e) => { setHover(true); props.onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHover(false); props.onMouseLeave?.(e); }}
      onFocus={(e) => { setFocus(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocus(false); props.onBlur?.(e); }}
    />
  );
}

export function UISelect(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & {
    width?: number | string; // e.g., 160 or "12rem"
  }
) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const BORDER_DEFAULT = "#c9ced6";
  const BORDER_HOVER = "#b3b9c3";
  const BORDER_FOCUS = "#9aa2af";
  const BG_SOFT = "#f5f6fa";

  const style: React.CSSProperties = {
    // width: props.width ?? "100%",
    height: 40,
    borderRadius: 10,
    padding: "8px 12px",
    border: "1px solid",
    borderColor: focus ? BORDER_FOCUS : hover ? BORDER_HOVER : BORDER_DEFAULT,
    background: BG_SOFT,
    outline: "none",
    transition: "border-color .15s ease",
    color: "#111827",
    // keep native arrow; if you want a custom chevron, add appearance: "none"
    ...props.style,
  };

  // pull width so it doesn't end up on the element
  const { width, ...rest } = props;

  return (
    <select
      {...rest}
      style={style}
      onMouseEnter={(e) => { setHover(true); props.onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHover(false); props.onMouseLeave?.(e); }}
      onFocus={(e) => { setFocus(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocus(false); props.onBlur?.(e); }}
    />
  );
}

type Option = { value: string; label?: string };
type UICustomSelectProps = {
  name: string;
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  options: Array<string | Option>;
  placeholder?: string;
  disabled?: boolean;
  /** Applied to the OUTER wrapper; use this for spacing, etc. */
  style?: React.CSSProperties;
  /** Forwarded to the OUTER wrapper; use Bootstrap grid here (e.g., "col-12 col-lg-2") */
  className?: string;
};
// export function UICustomSelect({
//   name,
//   value,
//   onChange,
//   options,
//   placeholder,
//   disabled,
//   style,
// }: {
//   name: string;
//   value: string;
//   onChange: (e: { target: { name: string; value: string } }) => void;
//   options: Array<string | Option>;
//   width?: number | string;
//   placeholder?: string;
//   disabled?: boolean;
//   style?: React.CSSProperties;
// }) {
//   const [open, setOpen] = useState(false);
//   const [hover, setHover] = useState(false);
//   const [focus, setFocus] = useState(false);
//   const [activeIdx, setActiveIdx] = useState<number>(-1);
//   const ref = useRef<HTMLDivElement | null>(null);

//   const normalized = useMemo<Option[]>(
//     () =>
//       options.map((o) =>
//         typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value }
//       ),
//     [options]
//   );

//   const BORDER_DEFAULT = "#c9ced6";
//   const BORDER_HOVER = "#b3b9c3";
//   const BORDER_FOCUS = "#9aa2af";
//   const BG_SOFT = "#f5f6fa";

//   // close on outside click
//   useEffect(() => {
//     const h = (e: MouseEvent) => {
//       if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
//     };
//     document.addEventListener("mousedown", h);
//     return () => document.removeEventListener("mousedown", h);
//   }, []);

//   // keyboard navigation
//   const onKeyDown = (e: React.KeyboardEvent) => {
//     if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
//       setOpen(true);
//       setActiveIdx(Math.max(0, normalized.findIndex((o) => o.value === value)));
//       e.preventDefault();
//       return;
//     }
//     if (!open) return;

//     if (e.key === "Escape") {
//       setOpen(false);
//       return;
//     }
//     if (e.key === "ArrowDown") {
//       setActiveIdx((i) => Math.min(normalized.length - 1, (i < 0 ? -1 : i) + 1));
//       e.preventDefault();
//     } else if (e.key === "ArrowUp") {
//       setActiveIdx((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
//       e.preventDefault();
//     } else if (e.key === "Enter") {
//       const opt = normalized[activeIdx];
//       if (opt) {
//         onChange({ target: { name, value: opt.value } });
//         setOpen(false);
//       }
//       e.preventDefault();
//     }
//   };

//   const triggerStyle: React.CSSProperties = {
//     height: 40,
//     borderRadius: 10,
//     padding: "8px 12px",
//     border: "1px solid",
//     borderColor: focus ? BORDER_FOCUS : hover ? BORDER_HOVER : BORDER_DEFAULT,
//     background: BG_SOFT,
//     outline: "none",
//     transition: "border-color .15s ease",
//     color: "#111827",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 8,
//     cursor: disabled ? "not-allowed" : "pointer",
//     ...style,
//   };

//   const label = normalized.find((o) => o.value === value)?.label ?? placeholder ?? "";

//   return (
//     <div ref={ref} style={{ position: "relative", width }}>
//       <div
//         role="button"
//         tabIndex={0}
//         onClick={() => !disabled && setOpen((o) => !o)}
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//         onFocus={() => setFocus(true)}
//         onBlur={() => setFocus(false)}
//         onKeyDown={onKeyDown}
//         style={triggerStyle}
//         aria-haspopup="listbox"
//         aria-expanded={open}
//       >
//         <span style={{ color: "#111827", fontSize: 14 }}>{label}</span>
//         <i className="bi bi-chevron-down" style={{ fontSize: 14, opacity: 0.7 }} />
//       </div>

//       {open && (
//         <div
//           role="listbox"
//           style={{
//             position: "absolute",
//             top: "calc(100% + 6px)",
//             left: 0,
//             width: "100%",
//             background: "#fff",
//             border: "1px solid #e5e7eb",
//             borderRadius: 12,
//             boxShadow: "0 16px 24px rgba(16,24,40,.14), 0 2px 8px rgba(16,24,40,.08)",
//             padding: 6,
//             zIndex: 30,
//             maxHeight: 336,
//             // overflowY: "auto",
//           }}
//         >
//           {normalized.map((opt, idx) => {
//             const selected = opt.value === value;
//             const isActive = idx === activeIdx;
//             return (
//               <button
//                 key={opt.value}
//                 role="option"
//                 aria-selected={selected}
//                 onMouseEnter={() => setActiveIdx(idx)}
//                 onClick={() => {
//                   onChange({ target: { name, value: opt.value } });
//                   setOpen(false);
//                 }}
//                 style={{
//                   width: "100%",
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "space-between",
//                   gap: 12,
//                   padding: "10px 12px",
//                   border: 0,
//                   borderRadius: 8,
//                   background: isActive ? "#f3f4f6" : "#fff", // full-row hover like your image
//                   cursor: "pointer",
//                   fontSize: 14,
//                   color: "#111827",
//                 }}
//               >
//                 <span>{opt.label}</span>
//                 {selected && <i className="bi bi-check2" style={{ color: "#111827" }} />}
//               </button>
//             );
//           })}
//         </div>
//       )}
//     </div>
//   );
// }

export function UICustomSelect({
  name,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  style,
  className = "",
}: UICustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const normalized = useMemo<Option[]>(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value }
      ),
    [options]
  );

  const BORDER_DEFAULT = "#c9ced6";
  const BORDER_HOVER = "#b3b9c3";
  const BORDER_FOCUS = "#9aa2af";
  const BG_SOFT = "#f5f6fa";

  // Close on outside click (but ignore clicks inside wrapper or menu)
  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  // Keyboard navigation
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      setOpen(true);
      setActiveIdx(Math.max(0, normalized.findIndex((o) => o.value === value)));
      e.preventDefault();
      return;
    }
    if (!open) return;

    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIdx((i) => Math.min(normalized.length - 1, (i < 0 ? -1 : i) + 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
      e.preventDefault();
    } else if (e.key === "Enter") {
      const opt = normalized[activeIdx];
      if (opt) {
        onChange({ target: { name, value: opt.value } });
        setOpen(false);
      }
      e.preventDefault();
    }
  };

  const triggerStyle: React.CSSProperties = {
    height: 40,
    borderRadius: 10,
    padding: "8px 12px",
    border: "1px solid",
    borderColor: focus ? BORDER_FOCUS : hover ? BORDER_HOVER : BORDER_DEFAULT,
    background: BG_SOFT,
    outline: "none",
    transition: "border-color .15s ease",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    cursor: disabled ? "not-allowed" : "pointer",
    width: "100%", // take full width of its container
  };

  const label = normalized.find((o) => o.value === value)?.label ?? placeholder ?? "";

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{ position: "relative", ...style }}
    >
      <div
        role="button"
        tabIndex={0}
        // Use onMouseDown to toggle open before document mousedown bubbles
        onMouseDown={(e) => {
          if (disabled) return;
          e.preventDefault(); // keep focus stable
          setOpen((o) => !o);
          if (!open) {
            setActiveIdx(Math.max(0, normalized.findIndex((o2) => o2.value === value)));
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyDown={onKeyDown}
        style={triggerStyle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ color: "#111827", fontSize: 14 }}>
          {label || <span style={{ opacity: 0.6 }}>Select…</span>}
        </span>
        <i className="bi bi-chevron-down" style={{ fontSize: 14, opacity: 0.7 }} />
      </div>

      {open && (
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: "100%",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 16px 24px rgba(16,24,40,.14), 0 2px 8px rgba(16,24,40,.08)",
            padding: 6,
            zIndex: 1030,      // higher than Bootstrap content around
            maxHeight: 336,
            overflowY: "auto",
          }}
        >
          {normalized.map((opt, idx) => {
            const selected = opt.value === value;
            const isActive = idx === activeIdx;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => {
                  onChange({ target: { name, value: opt.value } });
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 12px",
                  border: 0,
                  borderRadius: 8,
                  background: isActive ? "#f3f4f6" : "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  color: "#111827",
                }}
              >
                <span>{opt.label}</span>
                {selected && <i className="bi bi-check2" style={{ color: "#111827" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Toolbar shell ---------- */
export function Toolbar({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 20,
        width: "100%",
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 10,
        display: "flex",
        gap: 12,
        alignItems: "center",
        justifyContent: "flex-end",
        boxShadow: "0 1px 2px rgba(16,24,40,.06)",
        height: "87px"
      }}
    >
      {children}
    </div>
  );
}

/* ---------- Search pill with icon (uses UIInput inside) ---------- */
export function SearchBox({
  value,
  onChange,
  placeholder,
  width = 260,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width,
        height: 40,
        borderRadius: 10,
        padding: "0 8px",
        border: "1px solid #c9ced6",
        background: "#f5f6fa",
      }}
    >
      <span style={{ opacity: 0.6, fontSize: 14 }}>🔎</span>
      <UIInput
        value={value}
        onChange={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder={placeholder}
        style={{
          height: 34,
          border: "0",
          background: "transparent",
          boxShadow: "none",
        }}
      />
    </div>
  );
}

/* ---------- Toggle chip (matches pill look) ---------- */
export function ChipToggle({
  active,
  onToggle,
  children,
  icon,
}: {
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  const BORDER_DEFAULT = "#c9ced6";
  const BG_SOFT = "#f5f6fa";
  const BG_ON = "#ecfdf5";
  const TXT_ON = "#065f46";

  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        height: 40,
        padding: "0 12px",
        borderRadius: 999,
        border: `1px solid ${BORDER_DEFAULT}`,
        background: active ? BG_ON : BG_SOFT,
        color: active ? TXT_ON : "#1f2937",
        cursor: "pointer",
      }}
    >
      {icon ? <span style={{ opacity: 0.7 }}>{icon}</span> : null}
      {children}
    </button>
  );
}
